import type { TranslationKey } from '@/i18n';

/**
 * Central keyboard-shortcut registry (Phase 1 §43–44). Every shortcut is declared here so new
 * features can see — and not collide with — the existing key combinations.
 */
export interface KeyCombo {
  /** Value of `KeyboardEvent.key`, compared case-insensitively (e.g. `'s'`, `'Delete'`). */
  readonly key: string;
  /** Ctrl on Windows/Linux, Cmd on macOS. */
  readonly ctrl?: boolean;
  readonly shift?: boolean;
  readonly alt?: boolean;
}

export type ShortcutId = 'save' | 'undo' | 'redo' | 'delete' | 'escape' | 'toggleDeveloper';

export interface ShortcutDefinition {
  readonly id: ShortcutId;
  readonly combo: KeyCombo;
  readonly labelKey: TranslationKey;
  /** If false the shortcut is ignored while the user types in an input, so native editing works. */
  readonly allowInTextInput: boolean;
}

export const SHORTCUTS: readonly ShortcutDefinition[] = [
  {
    id: 'save',
    combo: { key: 's', ctrl: true },
    labelKey: 'shortcut.save',
    allowInTextInput: true,
  },
  {
    id: 'undo',
    combo: { key: 'z', ctrl: true },
    labelKey: 'shortcut.undo',
    allowInTextInput: false,
  },
  {
    id: 'redo',
    combo: { key: 'y', ctrl: true },
    labelKey: 'shortcut.redo',
    allowInTextInput: false,
  },
  { id: 'delete', combo: { key: 'Delete' }, labelKey: 'shortcut.delete', allowInTextInput: false },
  { id: 'escape', combo: { key: 'Escape' }, labelKey: 'shortcut.escape', allowInTextInput: true },
  {
    id: 'toggleDeveloper',
    combo: { key: 'd', ctrl: true, shift: true },
    labelKey: 'shortcut.toggleDeveloper',
    allowInTextInput: true,
  },
];

type KeyEventLike = Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>;

export function matchesCombo(event: KeyEventLike, combo: KeyCombo): boolean {
  return (
    event.key.toLowerCase() === combo.key.toLowerCase() &&
    (event.ctrlKey || event.metaKey) === (combo.ctrl ?? false) &&
    event.shiftKey === (combo.shift ?? false) &&
    event.altKey === (combo.alt ?? false)
  );
}

export function findShortcut(
  event: KeyEventLike,
  shortcuts: readonly ShortcutDefinition[] = SHORTCUTS,
): ShortcutDefinition | undefined {
  return shortcuts.find((shortcut) => matchesCombo(event, shortcut.combo));
}

/** Human-readable form of a combo, e.g. `Ctrl+Shift+D`. */
export function formatCombo(combo: KeyCombo): string {
  const key = combo.key.length === 1 ? combo.key.toUpperCase() : combo.key;
  return [combo.ctrl ? 'Ctrl' : null, combo.shift ? 'Shift' : null, combo.alt ? 'Alt' : null, key]
    .filter((part): part is string => part !== null)
    .join('+');
}

/** True when keyboard input is going to a text-editing element. */
export function isTextInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range'].includes(target.type);
  }
  return false;
}
